import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Card,
  CardBody,
  CardHeader,
  Divider,
  HStack,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Switch,
  Text,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react'
import { FiActivity } from 'react-icons/fi'
import { useMutation } from '@tanstack/react-query'
import { formatDistanceToNow } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { useApiError } from '@/hooks/useApiError'
import { useInterval } from '@/hooks/useInterval'
import { useToast } from '@/hooks/useToast'
import { formatChainName } from '@/constants/chains'
import { economyService } from '@/services/admin/economyService'
import type { TronContractUsageSnapshot, TronFeeData } from '@/types'
import { useNetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { ChainBadge } from '@/components/common/ChainBadge'
import { RefreshIconButton } from '@/components/common/RefreshIconButton'

const formatTrx = (value?: number | null) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '--'
  }
  if (value < 0.000001) {
    return value.toExponential(2)
  }
  return value.toFixed(6).replace(/\.?0+$/, '')
}

const formatSun = (value?: number | null) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '--'
  }
  return value.toLocaleString('en-US')
}

const formatTimestamp = (timestamp?: string | number) => {
  if (!timestamp) return '-'
  try {
    return formatDistanceToNow(new Date(timestamp), { addSuffix: true })
  } catch {
    return '-'
  }
}

export const TronFeeMonitor: React.FC = () => {
  const { t } = useTranslation()
  const toast = useToast()
  const { handleError } = useApiError()
  const { currentEnv } = useNetworkEnvironment()
  const bgColor = useColorModeValue('white', 'gray.800')
  const cardBg = useColorModeValue('gray.50', 'gray.900')
  const expectedChainIds = useMemo(
    () => (currentEnv === 'testnet' ? ['3448148188'] : ['728126428']),
    [currentEnv]
  )

  const [fees, setFees] = useState<Record<string, TronFeeData>>({})
  const [lastDeployment, setLastDeployment] = useState<TronContractUsageSnapshot | null>(null)
  const [lastInteraction, setLastInteraction] = useState<TronContractUsageSnapshot | null>(null)
  const [autoRefresh, setAutoRefresh] = useState(false)
  const [refreshCountdown, setRefreshCountdown] = useState(300)
  const [error, setError] = useState<any>(null)

  const mutation = useMutation({
    mutationFn: async () => {
      return await economyService.refreshTronFees({ chainIds: expectedChainIds }, currentEnv)
    },
    onSuccess: (response) => {
      const normalized: Record<string, TronFeeData> = {}
      expectedChainIds.forEach(chainId => {
        normalized[chainId] = { success: false }
      })
      Object.entries(response?.data ?? {}).forEach(([chainId, payload]) => {
        if (payload && Object.keys(payload).length > 0) {
          normalized[chainId] = payload as TronFeeData
        } else if (!normalized[chainId]) {
          normalized[chainId] = { success: false }
        }
      })
      setFees(normalized)
      setLastDeployment(response?.lastContractDeployment ?? null)
      setLastInteraction(response?.lastContractInteraction ?? null)
      setError(null)
      setRefreshCountdown(300)
    },
    onError: (err: any) => {
      setError(err)
      toast.error({
        title: t('monitoring.tronFee.error.title'),
        description: err.message || t('monitoring.tronFee.error.description'),
      })
      handleError(err, 'Tron fee refresh')
    },
  })

  const { mutate, isPending } = mutation

  const loadSnapshot = useCallback(async () => {
    try {
      const snapshot = await economyService.getTronFeesMonitor(currentEnv)
      const normalized: Record<string, TronFeeData> = {}
      expectedChainIds.forEach(chainId => {
        normalized[chainId] = { success: false }
      })
      Object.entries(snapshot?.data ?? {}).forEach(([chainId, payload]) => {
        if (payload && Object.keys(payload).length > 0) {
          normalized[chainId] = payload as TronFeeData
        } else if (!normalized[chainId]) {
          normalized[chainId] = { success: false }
        }
      })
      setFees(normalized)
      setLastDeployment(snapshot?.lastContractDeployment ?? null)
      setLastInteraction(snapshot?.lastContractInteraction ?? null)
      setError(null)
    } catch (err: any) {
      setError(err)
    }
  }, [currentEnv, expectedChainIds])

  useEffect(() => {
    const resetState = () => {
      setFees({})
      setLastDeployment(null)
      setLastInteraction(null)
      setError(null)
      setRefreshCountdown(300)
    }

    resetState()
    loadSnapshot()
  }, [currentEnv, loadSnapshot])

  useInterval(() => {
    if (autoRefresh && !isPending) {
      setRefreshCountdown(prev => {
        if (prev <= 1) {
          mutate()
          return 300
        }
        return prev - 1
      })
    }
  }, autoRefresh ? 1000 : null)

  const feeEntries = Object.entries(fees)
  const combinedChainId =
    lastDeployment?.chainId &&
    lastInteraction?.chainId &&
    lastDeployment.chainId === lastInteraction.chainId
      ? lastDeployment.chainId
      : null
  const formatUnits = (value?: number) => {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return '--'
    }
    return value.toLocaleString('en-US')
  }

  const formatFeeTrx = (value?: number | null) => formatTrx(value)

  const renderUsageDetails = (snapshot: TronContractUsageSnapshot | null) => {
    if (!snapshot) {
      return <Text color="gray.500">{t('monitoring.tronFee.lastUsage.empty')}</Text>
    }

    return (
      <VStack spacing={2} align="stretch">
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={2}>
          <Box>
            <Text fontSize="xs" color="gray.500">
              {t('monitoring.tronFee.lastUsage.energyUsed')}
            </Text>
            <Text fontSize="sm">{formatUnits(snapshot.energyUsed)}</Text>
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.500">
              {t('monitoring.tronFee.lastUsage.bandwidthUsed')}
            </Text>
            <Text fontSize="sm">{formatUnits(snapshot.bandwidthUsed)}</Text>
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.500">
              {t('monitoring.tronFee.lastUsage.feeTrx')}
            </Text>
            <Text fontSize="sm">{formatFeeTrx(snapshot.feeTrx)}</Text>
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.500">
              {t('monitoring.tronFee.lastUsage.totalSun')}
            </Text>
            <Text fontSize="sm">{formatSun(snapshot.totalSun)}</Text>
          </Box>
        </SimpleGrid>
        <Box>
          <Text fontSize="xs" color="gray.500">
            {t('monitoring.tronFee.lastUsage.updated')}
          </Text>
          <Text fontSize="sm">{formatTimestamp(snapshot.timestamp)}</Text>
        </Box>
      </VStack>
    )
  }

  const renderChainBadge = (chainId: string) => {
    const registryLabel = formatChainName(chainId, 'short')
    const chainCode = registryLabel && registryLabel !== 'Unknown'
      ? registryLabel
      : `Chain ${chainId}`

    return (
      <ChainBadge
        chainId={chainCode}
        chainCode={chainCode}
        chainNumericId={chainId}
        isTestnet={currentEnv === 'testnet'}
      />
    )
  }

  const renderUsageCard = (
    title: string,
    snapshot: TronContractUsageSnapshot | null
  ) => (
    <Card bg={cardBg} borderRadius="lg" variant="outline">
      <CardHeader pb={1}>
        <Text fontWeight="semibold">{title}</Text>
      </CardHeader>
      <CardBody pt={2} pb={2}>
        {renderUsageDetails(snapshot)}
      </CardBody>
    </Card>
  )

  const renderCombinedUsageCard = (
    chainId: string,
    deployment: TronContractUsageSnapshot | null,
    interaction: TronContractUsageSnapshot | null
  ) => {
    return (
      <Card bg={cardBg} borderRadius="lg" variant="outline">
        <CardHeader pb={1}>
          <HStack spacing={2}>
            {renderChainBadge(chainId)}
            <Text fontWeight="semibold">
              {t('monitoring.tronFee.lastUsage.contractUsage', {
                defaultValue: 'Contract Usage',
              })}
            </Text>
          </HStack>
        </CardHeader>
        <CardBody pt={2} pb={2}>
          <VStack spacing={2} align="stretch">
            <Box>
              <Text fontSize="sm" fontWeight="semibold" mb={1}>
                {t('monitoring.tronFee.lastUsage.deploymentTitle')}
              </Text>
              {renderUsageDetails(deployment)}
            </Box>
            <Divider />
            <Box>
              <Text fontSize="sm" fontWeight="semibold" mb={1}>
                {t('monitoring.tronFee.lastUsage.interactionTitle')}
              </Text>
              {renderUsageDetails(interaction)}
            </Box>
          </VStack>
        </CardBody>
      </Card>
    )
  }

  const renderUsageSection = (chainId: string) => {
    if (combinedChainId === chainId) {
      return renderCombinedUsageCard(chainId, lastDeployment, lastInteraction)
    }

    return (
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
        {renderUsageCard(
          t('monitoring.tronFee.lastUsage.deploymentTitle'),
          lastDeployment?.chainId === chainId ? lastDeployment : null
        )}
        {renderUsageCard(
          t('monitoring.tronFee.lastUsage.interactionTitle'),
          lastInteraction?.chainId === chainId ? lastInteraction : null
        )}
      </SimpleGrid>
    )
  }

  const renderFeeCard = (chainId: string) => {
    const data = fees[chainId] ?? { success: false }
    const resourceUnits = data.resourceUnits ?? {}
    const estimatedFees = data.estimatedFees ?? {}

    return (
      <Card key={chainId} bg={cardBg} borderRadius="lg" variant="outline">
        <CardHeader pb={1}>
          {renderChainBadge(chainId)}
        </CardHeader>
        <CardBody pt={2}>
          {data.success === false ? (
            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              <Text fontSize="sm">
                {data.error || t('monitoring.tronFee.unavailable')}
              </Text>
            </Alert>
          ) : (
            <VStack spacing={3} align="stretch">
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                <Stat>
                  <StatLabel fontSize="xs">{t('monitoring.tronFee.energyFee')}</StatLabel>
                  <StatNumber fontSize="lg">
                    {formatSun(data.energyFeeSun)} {t('monitoring.tronFee.sun')}
                  </StatNumber>
                </Stat>
                <Stat>
                  <StatLabel fontSize="xs">{t('monitoring.tronFee.bandwidthFee')}</StatLabel>
                  <StatNumber fontSize="lg">
                    {formatSun(data.bandwidthFeeSun)} {t('monitoring.tronFee.sun')}
                  </StatNumber>
                </Stat>
              </SimpleGrid>

              <Divider />

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                {([
                  { key: 'transfer', label: t('monitoring.tronFee.transfer') },
                  { key: 'trc20', label: t('monitoring.tronFee.trc20Transfer') },
                ] as const).map(({ key, label }) => {
                  const fee = estimatedFees[key]
                  const units = resourceUnits[key]
                  return (
                    <Box key={key} borderWidth="1px" borderRadius="md" p={2}>
                      <Text fontSize="xs" fontWeight="semibold" mb={1}>
                        {label}
                      </Text>
                      <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                        {formatTrx(fee?.trx)} {t('monitoring.tronFee.trx')}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {formatSun(fee?.sun)} {t('monitoring.tronFee.sun')}
                      </Text>
                      {units && (
                        <Text fontSize="xs" color="gray.500">
                          {t('monitoring.tronFee.energyUnits', { value: units.energy })} /{' '}
                          {t('monitoring.tronFee.bandwidthUnits', { value: units.bandwidth })}
                        </Text>
                      )}
                    </Box>
                  )
                })}
              </SimpleGrid>

              <Divider />

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                <Box>
                  <Text fontSize="xs" color="gray.500">
                    {t('monitoring.tronFee.source')}:
                  </Text>
                  <Text fontSize="sm">{data.source || t('monitoring.tronFee.unavailable')}</Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.500">
                    {t('monitoring.tronFee.lastUpdate')}:
                  </Text>
                  <Text fontSize="sm">{formatTimestamp(data.timestamp)}</Text>
                </Box>
              </SimpleGrid>
            </VStack>
          )}
        </CardBody>
      </Card>
    )
  }

  return (
    <VStack spacing={4} align="stretch">
      {error && !mutation.isPending && (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <Box flex="1">
            <AlertTitle>{t('monitoring.tronFee.error.title')}</AlertTitle>
            <AlertDescription>
              {error?.message || t('monitoring.tronFee.error.description')}
            </AlertDescription>
          </Box>
        </Alert>
      )}

      <Card bg={bgColor} borderRadius="lg">
        <CardHeader pb={2}>
          <HStack justify="space-between" align={{ base: 'flex-start', sm: 'center' }} flexWrap="wrap" gap={2}>
            <HStack minW={0} flex="1">
              <FiActivity />
              <Text fontSize="lg" fontWeight="semibold" overflowWrap="anywhere">
                {t('monitoring.tronFee.title')}
              </Text>
            </HStack>
            <HStack spacing={3} flexWrap="wrap" justify={{ base: 'flex-start', sm: 'flex-end' }}>
              <HStack>
                <Text fontSize="sm" color="gray.500">
                  {t('monitoring.tronFee.autoRefresh')}
                </Text>
                <Switch
                  isChecked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  size="sm"
                />
              </HStack>
              <RefreshIconButton
                label={
                  mutation.isPending
                    ? t('monitoring.tronFee.button.loading')
                    : t('monitoring.tronFee.button.refresh')
                }
                isLoading={mutation.isPending}
                onClick={() => mutation.mutate()}
              />
            </HStack>
          </HStack>
          <Text fontSize="sm" color="gray.500" mt={1}>
            {t('monitoring.tronFee.description')}
          </Text>
          {autoRefresh && (
            <Text fontSize="xs" color="gray.500" mt={1}>
              {t('monitoring.tronFee.nextRefresh', { seconds: refreshCountdown })}
            </Text>
          )}
        </CardHeader>
        <CardBody pt={2}>
          {feeEntries.length === 0 && !mutation.isPending ? (
            <Text color="gray.500">{t('monitoring.tronFee.empty')}</Text>
          ) : (
            <VStack align="stretch" spacing={5}>
              {expectedChainIds.map(chainId => (
                <VStack key={chainId} align="stretch" spacing={4}>
                  {renderUsageSection(chainId)}
                  {renderFeeCard(chainId)}
                </VStack>
              ))}
            </VStack>
          )}
        </CardBody>
      </Card>
    </VStack>
  )
}
